// Text-to-Speech utility.
//
// Strategy:
//   1. Prefer the free browser Web Speech API (`speechSynthesis`) when a voice
//      for the requested language is installed — best quality, offline capable.
//   2. Fall back to our own /api/tts backend endpoint (gTTS) when no native
//      voice is available. Windows/Chrome for example ships without Arabic
//      voices; this keeps Arabic (and every other supported language)
//      readable for every user without any paid API.

import { API } from '@/config';

const LANG_TAG_MAP = {
  ar: 'ar-SA',
  sv: 'sv-SE',
  en: 'en-GB',
  tr: 'tr-TR',
  fa: 'fa-IR',
  so: 'so-SO',
  pl: 'pl-PL',
  es: 'es-ES',
  de: 'de-DE',
};

// Arabic voices read noticeably slower than the others, so speed them up.
const BASE_SPEED = 1.5;
const LANG_SPEED = { ar: 1.75 };

let cachedVoices = null;
let currentAudio = null;
let speakSeq = 0;
// Saved ElevenLabs voices switch on only after every clip is generated; until then the old voices play.
let useStored = false;
if (typeof window !== 'undefined') {
  fetch(`${API}/tts/mode`).then((r) => r.json()).then((d) => { useStored = !!d.stored; }).catch(() => {});
}

const loadVoices = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      resolve([]);
      return;
    }
    if (cachedVoices && cachedVoices.length) {
      resolve(cachedVoices);
      return;
    }
    const voices = window.speechSynthesis.getVoices();
    if (voices && voices.length) {
      cachedVoices = voices;
      resolve(voices);
      return;
    }
    const timeout = setTimeout(() => {
      cachedVoices = window.speechSynthesis.getVoices() || [];
      resolve(cachedVoices);
    }, 500);
    window.speechSynthesis.onvoiceschanged = () => {
      clearTimeout(timeout);
      cachedVoices = window.speechSynthesis.getVoices() || [];
      resolve(cachedVoices);
    };
  });

const pickVoice = (voices, langTag) => {
  if (!voices || !voices.length) return null;
  const prefix = langTag.split('-')[0].toLowerCase();
  const candidates = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith(prefix));
  if (!candidates.length) return null;

  // Prefer a female voice for consistency with our backend TTS (which is
  // always female). Skip anything the browser explicitly labels as male.
  const FEMALE_HINTS = /(female|woman|feminine|zira|susan|samantha|victoria|karen|amira|salma|laila|rania|monika|paulina|maria|helena|nora|alva|klara|hedvig|petra|saga|linnea)/i;
  const MALE_HINTS = /(\bmale\b|\bman\b|masculine|david|alex|fred|george|daniel|thomas|oliver|karim|naayf|hamed|majed|majid|nariman|rasheed|mohammed|husam|marek|felipe|jorge|hans|bengt|erik|magnus|lars|gustav|johan|nils)/i;
  const female = candidates.find((v) => v.name && FEMALE_HINTS.test(v.name) && !MALE_HINTS.test(v.name));
  if (female) return female;
  const notMale = candidates.find((v) => !(v.name && MALE_HINTS.test(v.name)));
  if (notMale) return notMale;

  // Only male voices are available (e.g. Chrome-on-Windows Swedish "Bengt").
  // Return null so the caller falls back to our backend gTTS which is female
  // and gives a consistent voice across every language.
  return null;
};

// Warm the browser voice list up front so the first click doesn't wait for it.
if (typeof window !== 'undefined') loadVoices();

export const isTTSSupported = () => typeof window !== 'undefined';

export const stopSpeaking = () => {
  speakSeq += 1;
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  if (currentAudio) {
    try { currentAudio.pause(); } catch (e) { /* ignore */ }
    currentAudio.src = '';
    currentAudio = null;
  }
};

// Play MP3 audio streamed from our backend /api/tts endpoint.
const speakViaBackend = (text, lang, onStart, onEnd) => {
  const url = `${API}/tts?lang=${encodeURIComponent(lang)}&text=${encodeURIComponent(text)}`;
  const audio = new Audio(url);
  audio.playbackRate = LANG_SPEED[lang] || BASE_SPEED;
  currentAudio = audio;
  audio.onplaying = () => { if (onStart) onStart(); };
  audio.onended = () => { if (onEnd) onEnd(); };
  audio.onerror = () => { if (onEnd) onEnd(); };
  audio.play().catch(() => { if (onEnd) onEnd(); });
};

// Pre-generated ElevenLabs audio saved on our server. Resolves false if none is stored.
const speakStored = (text, lang, onStart, onEnd) => new Promise((resolve) => {
  const clean = String(text).replace(/\s+/g, ' ').trim();
  const audio = new Audio(`${API}/tts/stored?lang=${encodeURIComponent(lang)}&text=${encodeURIComponent(clean)}`);
  currentAudio = audio;
  let started = false;
  // 'playing' (not 'play'): 'play' fires immediately on play() even if the source later 404s.
  audio.onplaying = () => { if (started) return; started = true; if (onStart) onStart(); resolve(true); };
  audio.onended = () => { if (onEnd) onEnd(); };
  audio.onerror = () => { if (started) { if (onEnd) onEnd(); } else { if (currentAudio === audio) currentAudio = null; resolve(false); } };
  audio.play().catch(() => { if (!started) { if (currentAudio === audio) currentAudio = null; resolve(false); } });
});

// Speak a piece of text in the given app language.
export const speak = async (text, lang = 'en', { onStart, onEnd } = {}) => {
  if (!text) { if (onEnd) onEnd(); return; }
  stopSpeaking();
  const langTag = LANG_TAG_MAP[lang] || lang;
  const langPrefix = langTag.split('-')[0].toLowerCase();

  // 0) Saved natural voice (ElevenLabs), generated once on the server.
  const seq = speakSeq;
  if (useStored) {
    if (await speakStored(text, langPrefix, onStart, onEnd)) return;
    if (seq !== speakSeq) return;
  }

  // 1) Try native Web Speech API first (best quality, offline).
  if (typeof window !== 'undefined' && window.speechSynthesis && window.SpeechSynthesisUtterance) {
    const voices = await loadVoices();
    if (seq !== speakSeq) return;
    const voice = pickVoice(voices, langTag);
    if (voice) {
      const utter = new window.SpeechSynthesisUtterance(String(text));
      utter.lang = voice.lang || langTag;
      utter.rate = LANG_SPEED[langPrefix] || BASE_SPEED;
      utter.pitch = 0.95;
      utter.volume = 1;
      utter.voice = voice;
      if (onStart) utter.onstart = onStart;
      utter.onend = () => { if (onEnd) onEnd(); };
      utter.onerror = () => { if (onEnd) onEnd(); };
      window.speechSynthesis.speak(utter);
      return;
    }
  }

  // 2) Fallback: our free backend TTS proxy (works for every language).
  speakViaBackend(text, langPrefix, onStart, onEnd);
};
